import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AccionesDialog } from '@models/base/identificadores.model';
import { PeriodoContratoDetailDto } from '@models/periodos-contrato/periodoContratoDetailDto.model';
import { UpdatePeriodoContratoRequest } from '@models/periodos-contrato/updatePeriodoContratoRequest.model';
import { PeriodosContratoService } from '@services/periodos-contrato/periodos-contrato.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-dialog-update-periodo-contrato',
  templateUrl: './dialog-update-periodo-contrato.component.html',
  styleUrls: ['./dialog-update-periodo-contrato.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogUpdatePeriodoContratoComponent {

  // Constante para fecha SQL por defecto
  private readonly SQL_DEFAULT_DATE = '0001-01-01T00:00:00';
  periodoContratoForm!: FormGroup;
  periodoContratoDetailDto: PeriodoContratoDetailDto;
  selectedTab = 0;

  constructor(
    private fb: FormBuilder,
    private periodosContratoService: PeriodosContratoService,
    private spinner: NgxSpinnerService,
    private snackBar: SnackBarService,
    public dialogRef: MatDialogRef<DialogUpdatePeriodoContratoComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PeriodoContratoDetailDto
  ) {
    this.periodoContratoDetailDto = data;
    this.createForm();
  }

  ngOnInit(): void {
    this.loadFormData();
  }

  createForm(): void {
    this.periodoContratoForm = this.fb.group({
      periodo: ['', [Validators.required, Validators.min(1)]],
      periodoMes: ['', [Validators.required, Validators.min(1), Validators.max(12)]],
      periodoAnio: ['', [Validators.required, Validators.min(new Date().getFullYear() - 10), Validators.max(new Date().getFullYear() + 10)]],
      importeAlquiler: ['', [Validators.required, Validators.min(0.01)]],
      valorAjuste: ['', [Validators.min(0)]]
    });

    const tieneFechaPago = this.tieneFechaValida(this.periodoContratoDetailDto?.fechaPago);
    const tieneFechaLiquidacion = this.tieneFechaValida(this.periodoContratoDetailDto?.fechaLiquidacion);

    // Deshabilitar si está pagado o liquidado
    if (tieneFechaPago || tieneFechaLiquidacion) {
      this.periodoContratoForm.disable();
    } else {
      this.periodoContratoForm.enable();
    }
  }

  /**
 * Verifica si una fecha es válida (no null, no undefined, no fecha SQL por defecto)
 */
  private tieneFechaValida(fecha: string | null | undefined): boolean {
    if (!fecha) return false;
    if (fecha === this.SQL_DEFAULT_DATE) return false;

    // Validar que sea una fecha real
    const date = new Date(fecha);
    return !isNaN(date.getTime()) && date.getFullYear() > 1;
  }


  estaPagadoOLiquidado(): boolean {
    return this.esFechaValida(this.periodoContratoDetailDto?.fechaPago) ||
      this.esFechaValida(this.periodoContratoDetailDto?.fechaLiquidacion);
  }

  /**
   * Valida si una fecha tiene un valor real
   */
  private esFechaValida(fecha?: string | null): boolean {
    return !!fecha &&
      fecha !== '0001-01-01T00:00:00' &&
      new Date(fecha).getFullYear() > 1900;
  }

  loadFormData(): void {
    if (this.periodoContratoDetailDto) {
      this.periodoContratoForm.patchValue({
        periodo: this.periodoContratoDetailDto.periodo,
        periodoMes: this.periodoContratoDetailDto.periodoMes,
        periodoAnio: this.periodoContratoDetailDto.periodoAnio,
        importeAlquiler: this.periodoContratoDetailDto.importeAlquiler,
        valorAjuste: this.periodoContratoDetailDto.valorAjuste
      });
    }
  }

  get f() {
    return this.periodoContratoForm.controls;
  }

  onSubmit(): void {
  // Validar que el formulario sea válido y no esté pagado/liquidado
  if (!this.periodoContratoForm.valid) {
    this.snackBar.showError('Complete todos los campos correctamente', 'Error de validación');
    return;
  }

  if (this.estaPagadoOLiquidado()) {
    this.snackBar.showError('No se puede modificar un periodo pagado o liquidado', 'Acción no permitida');
    return;
  }

  const updateRequest: UpdatePeriodoContratoRequest = {
    id: this.periodoContratoDetailDto.id,
    periodo: this.f.periodo.value,
    periodoMes: this.f.periodoMes.value,
    periodoAnio: this.f.periodoAnio.value,
    importeAlquiler: this.f.importeAlquiler.value,
    valorAjuste: this.f.valorAjuste.value || 0
  };

  this.update(updateRequest);
}


  update(updatePeriodoContratoRequest: UpdatePeriodoContratoRequest) {
    this.spinner.show("spPeriodoContrato")
    this.periodosContratoService.update(updatePeriodoContratoRequest)
      .subscribe(
        data => {
          this.spinner.hide("spPeriodoContrato")
          this.snackBar.showSuccess("Periodo actualizado correctamente.", "Exito")
          this.dialogRef.close({ event: AccionesDialog.Modificar });
        },
        error => {
          this.spinner.hide("spPeriodoContrato")
          this.snackBar.showError(error, "Error");
        })
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }

  // Métodos para el datepicker de mes/año
  chosenYearHandler(normalizedYear: any): void {
    const ctrlValue = this.f.periodoAnio.value || new Date();
    ctrlValue.setFullYear(normalizedYear.getFullYear());
    this.f.periodoAnio.setValue(ctrlValue.getFullYear());
  }

  chosenMonthHandler(normalizedMonth: any, datepicker: any): void {
    const ctrlValue = this.f.periodoMes.value || new Date();
    ctrlValue.setMonth(normalizedMonth.getMonth());
    this.f.periodoMes.setValue(ctrlValue.getMonth() + 1);
    datepicker?.close();
  }

  getEstadoPeriodo(): string {
    if (this.periodoContratoDetailDto?.fechaLiquidacion) {
      return 'Liquidado';
    }
    if (this.periodoContratoDetailDto?.fechaPago) {
      return 'Pagado';
    }
    return 'Pendiente';
  }

  getIconoEstado(): string {
    if (this.periodoContratoDetailDto?.fechaLiquidacion) {
      return 'account_balance';
    }
    if (this.periodoContratoDetailDto?.fechaPago) {
      return 'check_circle';
    }
    return 'schedule';
  }

  getColorEstado(): string {
    if (this.periodoContratoDetailDto?.fechaLiquidacion) {
      return 'accent';
    }
    if (this.periodoContratoDetailDto?.fechaPago) {
      return 'primary';
    }
    return 'warn';
  }

  trackByItems(index: number, item: any): any {
    return item.id;
  }
}
